import React, { useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import { getIcon } from '../../utils/IconHandler';
import { useTranslation } from 'react-i18next';
import ViewUserModal from '../../pages/location_management/staff/ViewUserModal';
import EditUserModal from '../../pages/location_management/staff/EditUserModal';
import ResetPasswordModal from '../../pages/location_management/staff/ResetPasswordModal';
import { permissionOptions, getStatusById } from '../../utils/Enums';
import TableRefreshButton from '../TableRefreshButton/TableRefreshButton';

import './Staff.scss';

const getScopeLabel = (user) => {
  const scopes = user.scopes || [];
  if (!scopes.length) return 'Legacy';
  return scopes.map((scope) => {
    switch (scope.scopeType) {
      case 'ALL':
        return 'Ecosistema';
      case 'MAIN':
        return 'Main';
      case 'BUSINESS_UNIT':
        return scope.businessUnitName || 'Franquicia';
      case 'LOCATION':
        return 'Sucursales asignadas';
      case 'ASSIGNED_LOCATIONS':
        return 'Sucursales asignadas';
      default:
        return scope.scopeType;
    }
  }).join(', ');
};

export default function StaffTable({ users, onAddUser, onResetPassword, onEditUser, roles, loading, onRefresh, locations = [], businessUnits = [], isBusinessUnitScoped = false }) {
  
  const { t } = useTranslation();
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];

  const hasPermission = (permissionId) => userPermissions.includes(permissionId);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return Array.isArray(users) ? users : [];

    return (Array.isArray(users) ? users : []).filter((user) => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
      const email = String(user.email || '').toLowerCase();
      const phone = String(user.phone || '').toLowerCase();
      const roleName = String(user.role?.name || '').toLowerCase();
      const scope = getScopeLabel(user).toLowerCase();

      return (
        fullName.includes(normalizedSearch) ||
        email.includes(normalizedSearch) ||
        phone.includes(normalizedSearch) ||
        roleName.includes(normalizedSearch) ||
        scope.includes(normalizedSearch)
      );
    });
  }, [users, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setIsResetPasswordModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  return (
    <>
      <Paper sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden', p: 2, boxSizing: 'border-box' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
          <TextField
            label={t('search')}
            placeholder={t('search')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: 300 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <TableRefreshButton loading={loading} onClick={onRefresh} />
            {hasPermission(permissionOptions.find(p => p.label === 'CREATE_STAFF').id) && (
              <Button variant="outlined" startIcon={getIcon('add')} onClick={onAddUser}>
                {t('add_user')}
              </Button>
            )}
          </Box>
        </Box>
        <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1000 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell width="20%" style={{ minWidth: 200 }}><span className="roboto-regular">{t('name')}</span></TableCell>
                    <TableCell width="15%" align="center"><span className="roboto-regular">{t('phone')}</span></TableCell>
                    <TableCell width="25%" align="center"><span className="roboto-regular">{t('email')}</span></TableCell>
                    <TableCell width="15%" align="center"><span className="roboto-regular">{t('status')}</span></TableCell>
                    <TableCell width="15%" align="center"><span className="roboto-regular">{t('role')}</span></TableCell>
                    <TableCell width="20%" align="center"><span className="roboto-regular">Alcance</span></TableCell>
                    <TableCell width="15%" align="center"><span className="roboto-regular">Dashboard web</span></TableCell>
                    <TableCell width="20%" align="right" style={{ minWidth: 120 }}><span className="roboto-regular">{t('actions')}</span></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                        <TableRow>
                        <TableCell colSpan={9} align="center">
                            <Box py={3}>
                            <CircularProgress />
                            </Box>
                        </TableCell>
                        </TableRow>
                    ) : (
                      filteredUsers.length > 0
                        ? filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                          <TableRow key={user.id}>
                            <TableCell component="th" scope="row">
                              <Avatar alt={`${user.firstName} ${user.firstName}`} src={user.profilePictureUrl} />
                            </TableCell>
                            <TableCell style={{ minWidth: 200 }}><span className="roboto-light">{user.firstName} {user.lastName}</span></TableCell>
                            <TableCell align="center"><span className="roboto-light">{user.phone}</span></TableCell>
                            <TableCell align="center"><span className="roboto-light">{user.email}</span></TableCell>
                            <TableCell align="center"><span className="roboto-light">{t(getStatusById(user.status).label)}</span></TableCell>
                            <TableCell align="center"><Chip color="info" size="small" variant="outlined" label={user.role.name} /></TableCell>
                            <TableCell align="center"><Chip color="default" size="small" variant="outlined" label={getScopeLabel(user)} /></TableCell>
                            <TableCell align="center">
                              <Chip
                                color={user.canAccessWebDashboard ? 'success' : 'default'}
                                size="small"
                                variant="outlined"
                                label={user.canAccessWebDashboard ? 'Sí' : 'No'}
                              />
                            </TableCell>
                            <TableCell align="right" style={{ minWidth: 120 }}>
                              <IconButton onClick={() => handleViewUser(user)}>{getIcon('eye')}</IconButton>
                              {hasPermission(permissionOptions.find(p => p.label === 'UPDATE_STAFF').id) && (
                                <>
                                  <IconButton onClick={() => handleResetPassword(user)}>{getIcon('lock')}</IconButton>
                                  <IconButton onClick={() => handleEditUser(user)}>{getIcon('settings')}</IconButton>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                        : <TableRow>
                          <TableCell colSpan={9} align="center"><span className="roboto-light">{t('empty_users')}</span></TableCell>
                        </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {selectedUser && (
        <ViewUserModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          user={selectedUser}
        />
      )}

      {selectedUser && (
        <EditUserModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          user={selectedUser}
          onSave={(updatedUser) => onEditUser(selectedUser.email, updatedUser)}
          roles={roles}
          locations={locations}
          businessUnits={businessUnits}
          isBusinessUnitScoped={isBusinessUnitScoped}
        />
      )}

      {selectedUser && (
        <ResetPasswordModal
          open={isResetPasswordModalOpen}
          onClose={() => setIsResetPasswordModalOpen(false)}
          onSave={(newPassword) => onResetPassword(selectedUser.email, newPassword)}
        />
      )}
    </>
  );
}
